import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { Alert } from '@/components/ui/Alert';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { TeamMemberForm } from './TeamMemberForm';
import {
    useTeamMembers,
    useCreateTeamMember,
    useUpdateTeamMember,
    useDeleteTeamMember,
    useTeamMemberTaskCount,
} from '@/hooks';
import type { Database } from '@/types/database.types';
import type { TeamMemberFormData } from '@/utils/validation';

type TeamMember = Database['public']['Tables']['team_members']['Row'];

/**
 * Team member list component that displays all team members
 * Handles CRUD operations for team members
 */
export const TeamMemberList = () => {
    // Fetch team members
    const { data: teamMembers, isLoading, error: fetchError } = useTeamMembers();

    // Mutations
    const createTeamMember = useCreateTeamMember();
    const updateTeamMember = useUpdateTeamMember();
    const deleteTeamMember = useDeleteTeamMember();

    // UI state
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingMember, setEditingMember] = useState<TeamMember | undefined>(undefined);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Fetch task count for member being deleted
    const { data: taskCount } = useTeamMemberTaskCount(memberToDelete?.id ?? 0);

    // Handle add team member
    const handleAddClick = useCallback(() => {
        setEditingMember(undefined);
        setIsFormOpen(true);
    }, []);

    // Handle edit team member
    const handleEditClick = useCallback((member: TeamMember) => {
        setEditingMember(member);
        setIsFormOpen(true);
    }, []);

    // Handle remove team member click
    const handleRemoveClick = useCallback((member: TeamMember) => {
        setMemberToDelete(member);
        setDeleteConfirmOpen(true);
    }, []);

    // Handle form close
    const handleFormClose = useCallback(() => {
        setIsFormOpen(false);
        setEditingMember(undefined);
    }, []);

    // Handle delete dialog close
    const handleDeleteDialogClose = useCallback(() => {
        setDeleteConfirmOpen(false);
        setMemberToDelete(null);
    }, []);

    // Handle form submission
    const handleFormSubmit = async (formData: TeamMemberFormData) => {
        try {
            if (editingMember) {
                // Update existing team member
                await updateTeamMember.mutateAsync({
                    id: editingMember.id,
                    updates: {
                        name: formData.name,
                        role: formData.role as 'developer' | 'qa',
                    },
                });
                setSuccessMessage('Team member updated successfully!');
            } else {
                // Create new team member
                // Note: user_id will be set by RLS policies in Supabase
                await createTeamMember.mutateAsync({
                    name: formData.name,
                    role: formData.role as 'developer' | 'qa',
                });
                setSuccessMessage('Team member added successfully!');
            }
            setIsFormOpen(false);
            setEditingMember(undefined);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            // Error is handled by React Query and passed to the form
            console.error('Form submission error:', err);
        }
    };

    // Handle delete confirmation
    const handleDeleteConfirm = async () => {
        if (!memberToDelete) return;

        try {
            await deleteTeamMember.mutateAsync(memberToDelete.id);
            setSuccessMessage('Team member removed successfully!');
            setDeleteConfirmOpen(false);
            setMemberToDelete(null);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Delete error:', err);
        }
    };

    // Format role for display
    const formatRole = (role: 'developer' | 'qa') => {
        return role === 'developer' ? 'Developer' : 'QA';
    };

    // Get badge variant for role
    const getRoleBadgeVariant = (role: 'developer' | 'qa'): 'info' | 'on-track' => {
        return role === 'developer' ? 'info' : 'on-track';
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-64">
                <Spinner size="lg" />
            </div>
        );
    }

    // Error state
    if (fetchError) {
        return (
            <Alert variant="error">
                Failed to load team members. Please try again later.
                <br />
                <span className="text-sm">{fetchError.message}</span>
            </Alert>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Team Members</h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Manage your team members and their roles
                    </p>
                </div>
                <Button onClick={handleAddClick} variant="primary">
                    Add Team Member
                </Button>
            </div>

            {/* Success Message */}
            {successMessage && <Alert variant="success">{successMessage}</Alert>}

            {/* Team Members List */}
            {teamMembers && teamMembers.length > 0 ? (
                <Card>
                    <CardHeader>
                        <h3 className="text-lg font-semibold text-gray-900">
                            All Team Members ({teamMembers.length})
                        </h3>
                    </CardHeader>
                    <CardBody>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                                        >
                                            Name
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                                        >
                                            Role
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider"
                                        >
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {teamMembers.map((member) => (
                                        <tr
                                            key={member.id}
                                            className="hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {member.name}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Badge variant={getRoleBadgeVariant(member.role)}>
                                                    {formatRole(member.role)}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => handleEditClick(member)}
                                                        aria-label={`Edit ${member.name}`}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleRemoveClick(member)}
                                                        aria-label={`Remove ${member.name}`}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardBody>
                </Card>
            ) : (
                <Card>
                    <CardBody>
                        <EmptyState
                            title="No team members"
                            description="Get started by adding your first team member."
                            action={{
                                label: 'Add Team Member',
                                onClick: handleAddClick,
                            }}
                        />
                    </CardBody>
                </Card>
            )}

            {/* Team Member Form Modal */}
            <TeamMemberForm
                isOpen={isFormOpen}
                onClose={handleFormClose}
                onSubmit={handleFormSubmit}
                initialData={editingMember}
                isLoading={createTeamMember.isPending || updateTeamMember.isPending}
                error={
                    createTeamMember.error?.message || updateTeamMember.error?.message || null
                }
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirmOpen}
                onClose={handleDeleteDialogClose}
                onConfirm={handleDeleteConfirm}
                title="Remove Team Member"
                message={
                    memberToDelete ? `Are you sure you want to remove ${memberToDelete.name}?${taskCount?.data !== null &&
                        taskCount?.data !== undefined &&
                        taskCount.data > 0
                        ? `\n\nWarning: This team member has ${taskCount.data} assigned ${taskCount.data === 1 ? 'task' : 'tasks'
                        }. Removing this member will unassign these tasks.`
                        : ''
                        }` : ''
                }
                confirmText="Remove"
                variant="danger"
                isLoading={deleteTeamMember.isPending}
            />
        </div>
    );
};

TeamMemberList.displayName = 'TeamMemberList';

